import { ApiClient } from './ApiClient';
import { ApiError } from './ApiError';

export interface FetchApiClientOptions {
  baseUrl?: string;
  getAuthToken: () => string | Promise<string>;
}

// v1: bearer token via URL param + sessionStorage. v2: session tokens from App Bridge.
export const TOKEN_STORAGE_KEY = 'fjord.adminToken';

export function readTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (token) {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    const cleaned = new URL(window.location.href);
    cleaned.searchParams.delete('token');
    window.history.replaceState({}, '', cleaned.toString());
    return token;
  }
  return sessionStorage.getItem(TOKEN_STORAGE_KEY);
}

export class FetchApiClient implements ApiClient {
  private readonly baseUrl: string;
  private readonly getAuthToken: () => string | Promise<string>;

  constructor(opts: FetchApiClientOptions) {
    this.baseUrl = opts.baseUrl ?? '';
    this.getAuthToken = opts.getAuthToken;
  }

  async get<T>(path: string, opts?: RequestInit): Promise<T> {
    return this.request<T>('GET', path, undefined, opts);
  }

  async post<T>(path: string, body?: unknown, opts?: RequestInit): Promise<T> {
    return this.request<T>('POST', path, body, opts);
  }

  async put<T>(path: string, body?: unknown, opts?: RequestInit): Promise<T> {
    return this.request<T>('PUT', path, body, opts);
  }

  async delete<T>(path: string, opts?: RequestInit): Promise<T> {
    return this.request<T>('DELETE', path, undefined, opts);
  }

  private async request<T>(
    method: string,
    path: string,
    body: unknown,
    opts?: RequestInit,
  ): Promise<T> {
    const token = await this.getAuthToken();
    const headers = new Headers(opts?.headers);
    headers.set('Accept', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (body !== undefined) headers.set('Content-Type', 'application/json');

    const url = path.startsWith('http') ? path : `${this.baseUrl}${path}`;
    const response = await fetch(url, {
      ...opts,
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (response.status === 204) {
      return undefined as T;
    }

    const text = await response.text();
    const parsed = text.length > 0 ? safeJsonParse(text) : undefined;

    if (!response.ok) {
      const message =
        (typeof parsed === 'object' && parsed !== null && 'error' in parsed
          ? String((parsed as Record<string, unknown>).error)
          : response.statusText) || 'Request failed';
      throw new ApiError(response.status, message, parsed);
    }

    return parsed as T;
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
